"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useSession } from "next-auth/react";
import { Link, getPathname } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import useSolopreneurProfile, { useEntrepriseChoisie, useHydrate, useIdentite } from "@/hooks/useSolopreneurProfile";
import useDossierCompte from "@/hooks/useDossierCompte";
import { CHAMPS_SITUATION, differences, dossierVide, identiteVide, outilsQuiLisent, valeursPourOutil } from "@/lib/tools/dossier";
import { alertesAffichees, situationDuDossier, outilsDuDossier } from "@/lib/tools/dossierSituation";
import { dateLongue, euros, libelleChamp, libelleEcheance, valeurLisible } from "@/lib/tools/dossierTextes";
import { T, dateCourte, kickerAlerte, morceauxDossier, nJours, pourcent, remplir, texteAlerte, valeurQuestion } from "./situationTextes";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import { readLocal, writeLocal } from "@/helpers/safeStorage";
import { calendrierIcs } from "@/lib/tools/agendaIcs";
import ToolsDossierQuestions from "./ToolsDossierQuestions";
import ToolsIdentitySheet from "./ToolsIdentity";
import ToolsAccountSheet from "./ToolsAccountSheet";
import ToolsVraiSolde from "./ToolsVraiSolde";
import ToolsShareCard from "./ToolsShareCard";

/**
 * « Votre situation » : ce que les outils disent, ensemble, de celui qui a
 * répondu aux six questions.
 *
 * CHAQUE OUTIL RÉPOND À UNE QUESTION ; CETTE PAGE RÉPOND À CELLES QU'ON NE
 * S'EST PAS POSÉES. Personne ne pense à vérifier le seuil de TVA en septembre,
 * ni la date limite du versement libératoire. La page le fait à sa place et
 * met en tête ce qui presse : une date qui approche, un seuil qui tombe, un
 * gain à portée de main.
 *
 * TOUT EST CALCULÉ AILLEURS (lib/tools/dossierSituation.js), par les moteurs
 * des outils. Cette page ne fait que mettre des mots dessus, dans la langue du
 * lecteur, et renvoyer au bon outil pour le détail.
 *
 * LE DOSSIER RESTE SUR L'APPAREIL tant que le lecteur ne s'est pas connecté.
 * Le compte n'ajoute que ce qu'un serveur peut faire : retrouver le dossier
 * ailleurs, l'agenda auquel on s'abonne, les alertes chiffrées.
 */


// Le langage de /outils : une carte blanche franche posée sur le dégradé,
// des listes à filets, des titres de section en petites capitales.
const CARTE = "rounded-2xl border border-gray-300/80 dark:border-white/15 bg-white dark:bg-white/[0.05] shadow-[0_10px_30px_rgba(17,24,39,0.05)] p-5 sm:p-7";
const LISTE = "overflow-hidden rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03]";
const TITRE_SECTION = "mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-grayDark-400";
const PASTILLE =
    "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] px-3.5 text-sm font-semibold text-gray-800 dark:text-white transition-colors duration-200 hover:border-gray-300 dark:hover:border-white/25";
const KICKER = "text-xs font-bold uppercase tracking-wider";
const LIEN = "inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary transition-colors duration-200";
const BOUTON_PLEIN =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 dark:bg-white px-5 py-3 text-sm font-semibold text-white dark:text-gray-900 transition-opacity hover:opacity-90";
const BOUTON_VIDE =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-white/15 px-5 py-3 text-sm font-semibold text-gray-700 dark:text-lightBlue hover:border-gray-300 dark:hover:border-white/30 transition-colors duration-200";

/** Les tons des cartes d'alerte : ambre qui presse, bleu qui informe. */
const TONS = {
    urgent: {
        point: "bg-red-500",
        cadre: "border-amber-300/70 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/[0.07]",
        kicker: "text-amber-800 dark:text-amber-300",
        texte: "text-amber-900/90 dark:text-amber-100/90",
    },
    attention: {
        point: "bg-amber-500",
        cadre: "border-amber-200/80 dark:border-amber-400/20 bg-amber-50/60 dark:bg-amber-400/[0.04]",
        kicker: "text-amber-800 dark:text-amber-300",
        texte: "text-amber-900/90 dark:text-amber-100/90",
    },
    info: {
        point: "bg-blue-500",
        cadre: "border-blue-200/80 dark:border-blue-400/20 bg-blue-50 dark:bg-blue-400/[0.06]",
        kicker: "text-blue-800 dark:text-blue-300",
        texte: "text-blue-900/90 dark:text-blue-100/90",
    },
};

function LienOutil({ outils, cle, children }) {
    const o = outils?.[cle];
    if (!o) return null;
    return (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: o.slug } }} className={LIEN}>
            {children}
            <SolarIcon name="arrow-right-linear" className="h-4 w-4" />
        </Link>
    );
}

function telechargerIcs(echeances, lang, outils) {
    const ics = calendrierIcs(echeances, {
        lang,
        lienOutil: (cle) =>
            outils?.[cle]
                ? `${window.location.origin}${getPathname({ locale: lang, href: { pathname: "/tools/[slug]", params: { slug: outils[cle].slug } } })}`
                : null,
    });
    const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "believemy-echeances.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Une confirmation dans une feuille qui glisse du bas, à la place des boîtes
 * du navigateur (window.confirm) : même design que le reste, et un bouton
 * « Annuler » toujours à portée.
 */
function Confirmer({ demande, onFermer, t }) {
    return (
        <BottomSheetModal isOpen={Boolean(demande)} onClose={onFermer} labelledBy="dossier-confirmer-titre">
            {demande && (
                <div className="p-6 sm:p-8">
                    <h2 id="dossier-confirmer-titre" className="mb-2 text-xl font-bold font-sourceSans dark:text-white">
                        {demande.titre}
                    </h2>
                    <p className="mb-6 text-sm sm:text-base text-gray-600 dark:text-lightBlue">{demande.texte}</p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={onFermer} className={BOUTON_VIDE}>
                            {demande.non || t.annuler}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                demande.onOui();
                                onFermer();
                            }}
                            className={
                                demande.danger
                                    ? "inline-flex min-h-[44px] items-center justify-center rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white hover:bg-red-700"
                                    : BOUTON_PLEIN
                            }
                        >
                            {demande.oui}
                        </button>
                    </div>
                </div>
            )}
        </BottomSheetModal>
    );
}

/** « Plus tard » sur la carte des réponses manquantes : trente jours de répit, sur cet appareil. */
const CLE_PLUS_TARD = "believemy-dossier-plus-tard";
function plusTardActif() {
    const le = readLocal(CLE_PLUS_TARD);
    return Boolean(le) && Date.now() - Date.parse(le) < 30 * 86400000;
}

/** Le nombre d'outils que chaque réponse manquante remplirait, calculé une fois. */
const OUTILS_PAR_MANQUE = {
    statut: outilsQuiLisent(["statut"]).length,
    activite: outilsQuiLisent(["activite", "artisan"]).length,
    dateDebut: outilsQuiLisent(["dateDebut"]).length,
    caCetteAnnee: outilsQuiLisent(["caCetteAnnee", "caAnDernier"]).length,
    foyer: outilsQuiLisent(["enCouple", "enfants", "autresRevenus"]).length,
    objectifMensuel: outilsQuiLisent(["objectifMensuel", "fraisMensuels"]).length,
};
/** La question qui répond à chaque manque. */
const QUESTION_DU_MANQUE = { statut: "statut", activite: "activite", dateDebut: "debut", caCetteAnnee: "ca", foyer: "foyer", objectifMensuel: "objectif" };

/**
 * VOS PROCHAINES DATES, EN CALENDRIER. Chaque date a sa pastille jour-mois,
 * son libellé, le temps qui reste, l'outil qui la traite et « C'est fait ».
 * Les quatre premières d'abord ; les autres se déplient. L'export vers
 * l'agenda ferme la liste, là où l'on a fini de la lire.
 */
function Agenda({ r, t, lang, outils, compte, confirmer, marquerFait }) {
    const [ouvert, setOuvert] = useState(false);
    const [copie, setCopie] = useState(false);
    const [renouvellement, setRenouvellement] = useState(null);

    const renouveler = () =>
        confirmer({
            titre: t.renouvelerTitre,
            texte: t.renouvelerConfirmer,
            oui: t.renouvelerOui,
            onOui: async () => {
                setRenouvellement("envoi");
                try {
                    await compte.renouvelerAgenda();
                    setRenouvellement("ok");
                } catch (e) {
                    setRenouvellement("erreur");
                }
            },
        });

    const copier = async () => {
        try {
            await navigator.clipboard.writeText(compte.agenda);
            setCopie(true);
            setTimeout(() => setCopie(false), 2500);
        } catch (e) {
            // Le presse-papiers refusé : l'adresse reste affichée, à copier à la main.
        }
    };

    const loc = lang === "en" ? "en-GB" : "fr-FR";
    const visibles = ouvert ? r.echeances : r.echeances.slice(0, 4);

    return (
        <section aria-labelledby="situation-dates">
            <h2 id="situation-dates" className={TITRE_SECTION}>
                <SolarIcon name="calendar-minimalistic-linear" className="h-4 w-4" />
                {t.vosDates}
            </h2>
            <div className={LISTE}>
                {r.echeances.length === 0 ? (
                    <p className="m-0 px-5 py-4 text-sm text-gray-600 dark:text-lightBlue">
                        {r.manque.includes("dateDebut") ? t.echeanceManque : t.echeanceAucune}
                    </p>
                ) : (
                    <>
                        <ul className="m-0 list-none divide-y divide-gray-200/60 dark:divide-white/[0.07] p-0">
                            {visibles.map((e, i) => {
                                const jour = new Date(`${e.date}T12:00:00`);
                                const libelle = libelleEcheance(e.cle, e.famille, lang);
                                const proche = e.joursRestants <= 30;
                                return (
                                    <li key={`${e.cle}-${e.date}`} className="flex items-start gap-4 px-4 py-3.5 sm:px-5">
                                        <span
                                            className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl border ${
                                                i === 0 ? "border-primary/30 bg-[#fff4ee] dark:bg-[#D04515]/15" : "border-gray-200/80 bg-white dark:border-white/10 dark:bg-white/[0.04]"
                                            }`}
                                        >
                                            <span className={`text-lg font-bold leading-none tabular-nums font-sourceSans ${i === 0 ? "text-[#B83C12] dark:text-[#FF8A5B]" : "text-gray-900 dark:text-white"}`}>
                                                {jour.getDate()}
                                            </span>
                                            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">
                                                {new Intl.DateTimeFormat(loc, { month: "short" }).format(jour).replace(".", "")}
                                            </span>
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="mb-0 text-[15px] font-semibold leading-snug text-gray-900 dark:text-white">{libelle}</p>
                                            <p className={`mb-0 mt-0.5 text-[13px] ${proche ? "font-semibold text-[#B83C12] dark:text-[#FF8A5B]" : "text-gray-500 dark:text-lightBlue/70"}`}>
                                                {e.joursRestants === 0 ? t.aujourdhui : remplir(t.dansJours, { jours: nJours(e.joursRestants, lang) })}
                                                {Number(e.date.slice(0, 4)) !== Number(r.aujourdhui.slice(0, 4)) ? ` · ${e.date.slice(0, 4)}` : ""}
                                            </p>
                                            <div className="mt-0.5 flex flex-wrap items-center gap-x-4">
                                                {outils?.[e.outil] && (
                                                    <Link
                                                        href={{ pathname: "/tools/[slug]", params: { slug: outils[e.outil].slug } }}
                                                        className="inline-flex min-h-[36px] items-center gap-1 text-[13px] font-semibold text-gray-700 dark:text-lightBlue underline decoration-gray-300 underline-offset-4 hover:decoration-primary"
                                                    >
                                                        {outils[e.outil].nom}
                                                        <SolarIcon name="arrow-right-linear" className="h-3.5 w-3.5" />
                                                    </Link>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => marquerFait(e.fait, libelle)}
                                                    className="inline-flex min-h-[36px] items-center gap-1 text-[13px] font-semibold text-gray-500 dark:text-lightBlue/70 hover:text-gray-900 dark:hover:text-white"
                                                >
                                                    <SolarIcon name="check-circle-linear" className="h-3.5 w-3.5" />
                                                    {t.cestFait}
                                                    <span className="sr-only"> : {libelle}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        {r.echeances.length > 4 && (
                            <button
                                type="button"
                                onClick={() => setOuvert((o) => !o)}
                                aria-expanded={ouvert}
                                className="flex w-full min-h-[44px] items-center justify-center gap-1 border-t border-gray-200/60 dark:border-white/[0.07] text-sm font-semibold text-gray-700 dark:text-lightBlue hover:bg-white/60 dark:hover:bg-white/[0.03]"
                            >
                                {ouvert ? t.moinsDeDates : remplir(t.plusDeDates, { n: r.echeances.length - 4 })}
                                <SolarIcon name={ouvert ? "alt-arrow-up-linear" : "alt-arrow-down-linear"} className="h-4 w-4" />
                            </button>
                        )}
                        <div className="flex flex-col gap-2 border-t border-gray-200/60 dark:border-white/[0.07] bg-gray-50/60 dark:bg-white/[0.02] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                            <span className="text-xs text-gray-500 dark:text-lightBlue/70">{t.telechargerAide}</span>
                            <button type="button" onClick={() => telechargerIcs(r.echeances, lang, outils)} className={`${PASTILLE} shrink-0`}>
                                <SolarIcon name="calendar-add-linear" className="h-4 w-4 text-gray-500 dark:text-lightBlue/70" />
                                {t.telecharger}
                            </button>
                        </div>
                        {compte.connecte && compte.agenda && (
                            <div className="border-t border-gray-200/60 dark:border-white/[0.07] px-5 py-4">
                                <div className="text-sm font-semibold dark:text-white">{t.abonner}</div>
                                <p className="mt-1 mb-2 text-xs text-gray-500 dark:text-lightBlue/70">{t.abonnerAide}</p>
                                <div className="flex items-center gap-2">
                                    <input
                                        readOnly
                                        value={compte.agenda}
                                        onFocus={(e) => e.currentTarget.select()}
                                        aria-label={t.abonner}
                                        // En 16 pixels sur téléphone : en dessous, iOS zoome au focus.
                                        className="min-w-0 flex-1 min-h-[44px] rounded-lg border border-gray-200 dark:border-white/15 bg-white dark:bg-white/[0.04] px-2.5 py-2 text-base sm:text-xs text-gray-600 dark:text-lightBlue"
                                    />
                                    <button type="button" onClick={copier} className="shrink-0 min-h-[44px] rounded-lg bg-gray-900 dark:bg-white px-3 py-2 text-xs font-semibold text-white dark:text-gray-900">
                                        {copie ? t.copie : t.copier}
                                    </button>
                                    <span aria-live="polite" className="sr-only">
                                        {copie ? t.copie : ""}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={renouveler}
                                    disabled={renouvellement === "envoi"}
                                    className="mt-1 inline-flex min-h-[44px] items-center text-xs text-gray-500 dark:text-lightBlue/70 underline underline-offset-2 disabled:opacity-60"
                                >
                                    {t.renouveler}
                                </button>
                                <p aria-live="polite" className={`mb-0 text-xs ${renouvellement === "erreur" ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-lightBlue/70"}`}>
                                    {renouvellement === "ok" ? t.renouvele : renouvellement === "erreur" ? t.renouvelerErreur : ""}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}

function AlerteRegles({ t, lang, dossier }) {
    const { data: session } = useSession();
    const [etat, setEtat] = useState(null);
    const [email, setEmail] = useState("");
    const inscrire = async () => {
        setEtat("envoi");
        try {
            const reponse = await fetch("/api/tools/dossier/alerts", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dossier, locale: lang }),
            });
            if (!reponse.ok) throw new Error();
            const r = await reponse.json();
            setEmail(r.email);
            setEtat("ok");
        } catch (e) {
            setEtat("erreur");
        }
    };
    return (
        <div>
            <div className="flex items-start gap-3">
                <SolarIcon name="bell-linear" className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-lightBlue/60" />
                <div className="min-w-0">
                    <h2 className="mb-0 text-lg font-bold dark:text-white">{t.alerteTitre}</h2>
                    <p className="mt-1 mb-0 text-sm text-gray-600 dark:text-lightBlue">{t.alerteTexte}</p>
                    {etat !== "ok" && session?.user?.email && (
                        <p className="mt-2 mb-0 text-sm text-gray-600 dark:text-lightBlue">{remplir(t.alerteA, { email: session.user.email })}</p>
                    )}
                    {etat !== "ok" && (
                        <button type="button" onClick={inscrire} disabled={etat === "envoi"} className={`${BOUTON_PLEIN} mt-3 disabled:opacity-60`}>
                            {etat === "envoi" ? t.alerteEnvoi : t.alerteBouton}
                        </button>
                    )}
                    {/* Une région toujours présente, pour que la réponse soit annoncée. */}
                    <div aria-live="polite">
                        {etat === "ok" && (
                            <p className="mt-3 mb-0 flex items-center gap-2 text-sm text-gray-700 dark:text-lightBlue">
                                <SolarIcon name="check-circle-linear" className="h-4 w-4 text-primary" />
                                {remplir(t.alerteOk, { email })}
                            </p>
                        )}
                        {etat === "erreur" && <p className="mt-2 mb-0 text-sm text-red-600 dark:text-red-400">{t.alerteErreur}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ToolsSituation({ lang = "fr", outils = {}, aujourdhui }) {
    const t = T[lang] || T.fr;
    const hydrate = useHydrate();
    const { profil, effacer } = useSolopreneurProfile();
    const { identite } = useIdentite();
    const { entreprise: entrepriseGardee } = useEntrepriseChoisie();
    const compte = useDossierCompte(lang);

    // « parcours » : les six questions ; « une » : une seule, depuis le
    // récapitulatif ; « pret » : l'écran qui suit la dernière question.
    const [mode, setMode] = useState({ vue: null, question: null });
    const [feuille, setFeuille] = useState(false);
    // « Gardez votre dossier » : le lien de connexion par e-mail.
    const [feuilleCompte, setFeuilleCompte] = useState(false);
    // La part du compte vraiment à soi, tirée du vrai solde pour la carte.
    const [partVraie, setPartVraie] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    // La situation juste avant qu'on corrige une réponse : au retour, la page
    // dit ce que la correction a changé.
    const [avant, setAvant] = useState(null);
    const [plusTard, setPlusTard] = useState(false);

    const r = useMemo(() => situationDuDossier(profil, aujourdhui), [profil, aujourdhui]);
    const remplis = useMemo(
        () =>
            Object.entries(outils)
                .filter(([, o]) => o.composant && Object.keys(valeursPourOutil(o.composant, profil, aujourdhui)).length > 0)
                .map(([cle, o]) => ({ cle, ...o })),
        [outils, profil, aujourdhui],
    );

    // QUAND L'ÉCRAN CHANGE, LA PAGE SUIT. Passer de la synthèse, longue, à une
    // seule question laissait le lecteur mobile au pied de la page, et le
    // focus sur un bouton disparu. On remonte au début de la zone et on donne
    // le focus à son premier titre. Rien au premier affichage.
    const zone = useRef(null);
    const reduit = useReducedMotion();
    const premierAffichage = useRef(true);
    useEffect(() => {
        if (premierAffichage.current) {
            premierAffichage.current = false;
            return;
        }
        zone.current?.scrollIntoView({ block: "start", behavior: reduit ? "auto" : "smooth" });
        zone.current?.querySelector("h2")?.focus({ preventScroll: true });
    }, [mode.vue, mode.question, reduit]);

    // Le serveur ne connaît pas le dossier : pour lui, et pour le premier
    // rendu du navigateur, le dossier est vide et la page montre son accueil,
    // qui est le même pour tout le monde. Rien ne saute à l'hydratation pour
    // celui qui arrive sans dossier.
    const vide = !hydrate || dossierVide(profil);
    const vue = mode.vue || (vide ? "accueil" : "situation");
    const connecte = hydrate && compte.connecte;
    const alertes = alertesAffichees(r)
        .map((a) => ({ ...a, contenu: texteAlerte(a, r, t, lang) }))
        .filter((a) => a.contenu);
    const quitter = () => setMode({ vue: null, question: null });
    const { enregistrer } = useSolopreneurProfile();

    /** « C'est fait » : confirmé dans une feuille, puis rangé dans « En ordre ». */
    const marquerFait = (cle, titre) =>
        setConfirmation({
            titre: t.faitTitre,
            texte: remplir(t.faitTexte, { titre }),
            oui: t.faitOui,
            non: t.pasEncore,
            onOui: () => enregistrer({ faits: { ...(profil.faits || {}), [cle]: new Date().toISOString().slice(0, 10) } }),
        });
    const remettre = (cle) => {
        const faits = { ...(profil.faits || {}) };
        delete faits[cle];
        enregistrer({ faits });
    };

    // Ce qu'une correction a changé : les réponses, ce qu'il reste, les
    // alertes apparues ou réglées. Rien s'il n'y a rien à dire.
    const changements = useMemo(() => {
        if (!avant || mode.vue === "une") return null;
        const champs = differences(avant.dossier, profil).filter(({ champ }) => CHAMPS_SITUATION.includes(champ));
        const lignes = champs.map(({ champ, avant: a, apres }) =>
            `${libelleChamp(champ, lang)} : ${a === null || a === undefined ? "…" : valeurLisible(champ, a, lang)} → ${valeurLisible(champ, apres, lang)}`,
        );
        const apresReste = r.revenu?.disponibleMensuel ?? null;
        if (avant.disponible !== null && apresReste !== null && Math.round(avant.disponible) !== Math.round(apresReste)) {
            lignes.push(remplir(t.changementReste, { avant: euros(avant.disponible, lang), apres: euros(apresReste, lang) }));
        }
        const cles = new Set(alertes.map((a) => a.cle));
        const avantCles = new Set(avant.alertes.map((a) => a.cle));
        for (const a of alertes) if (!avantCles.has(a.cle)) lignes.push(remplir(t.changementNouveau, { titre: a.contenu.titre }));
        for (const a of avant.alertes) if (!cles.has(a.cle)) lignes.push(remplir(t.changementDisparu, { titre: a.titre }));
        return lignes.length ? lignes : null;
    }, [avant, mode.vue, profil, r, alertes, lang, t]);

    return (
        <div ref={zone} className="scroll-mt-24">
            {rendre()}
        </div>
    );

    function rendre() {

    if (vue === "accueil") {
        return (
            <div className={`${CARTE} max-w-2xl`}>
                <h2 tabIndex={-1} className="sr-only">{t.accueilTitre}</h2>
                <p className="mb-0 text-base sm:text-lg text-gray-700 dark:text-lightBlue">{t.vide}</p>
                {/* CE QU'ON OBTIENDRA, EN APERÇU. Trois cartes d'exemple, floues
                    et étiquetées comme telles : on voit la promesse avant de
                    répondre, sans qu'aucun chiffre ne passe pour le sien. */}
                <div className="relative mt-5" aria-hidden="true">
                    <div className="grid grid-cols-3 gap-2 blur-[2px] select-none opacity-70">
                        {[
                            ["2 870 €", t.exempleReste],
                            ["62 %", t.exempleTva],
                            [lang === "en" ? "15 Dec." : "15 déc.", t.exempleEcheance],
                        ].map(([chiffre, legende]) => (
                            <div key={legende} className="rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/[0.04] p-3">
                                <div className="text-lg sm:text-xl font-bold font-sourceSans tabular-nums dark:text-white">{chiffre}</div>
                                <div className="text-[11px] sm:text-xs text-gray-500 dark:text-lightBlue/70 leading-snug">{legende}</div>
                            </div>
                        ))}
                    </div>
                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-900 dark:bg-white px-3 py-1 text-xs font-semibold text-white dark:text-gray-900">
                        {t.exemple}
                    </span>
                </div>
                <p className="mt-4 mb-0 flex items-start gap-2 text-sm text-gray-500 dark:text-lightBlue/70">
                    <SolarIcon name="lock-keyhole-minimalistic-linear" className="mt-0.5 h-4 w-4 shrink-0" />
                    {connecte ? t.compteSynchro : t.confidentiel}
                </p>
                <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <button type="button" onClick={() => setMode({ vue: "parcours", question: null, premiere: true })} className={`${BOUTON_PLEIN} bg-[#D04515] dark:bg-[#D04515] text-white dark:text-white`}>
                        {t.commencer}
                        <SolarIcon name="arrow-right-linear" className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-500 dark:text-lightBlue/70">{t.accueilDuree}</span>
                </div>
            </div>
        );
    }

    if (vue === "parcours" || vue === "une") {
        return (
            <div className="max-w-2xl">
                <ToolsDossierQuestions
                    key={`${vue}-${mode.question || "debut"}`}
                    lang={lang}
                    depart={mode.question || "statut"}
                    seule={vue === "une"}
                    // L'écran « prêt » ne sert qu'au premier remplissage : après
                    // « Modifier mes réponses », on revient à la situation.
                    onTermine={() => setMode({ vue: vue === "parcours" && mode.premiere ? "pret" : "situation", question: null })}
                    onQuitter={quitter}
                />
                <p className="mt-4 mb-0 flex items-start gap-2 text-sm text-gray-500 dark:text-lightBlue/70">
                    <SolarIcon name="lock-keyhole-minimalistic-linear" className="mt-0.5 h-4 w-4 shrink-0" />
                    {connecte ? t.compteSynchro : t.confidentiel}
                </p>
            </div>
        );
    }

    if (vue === "pret") {
        return (
            <motion.div initial={reduit ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: "easeOut" }} className={`${CARTE} max-w-2xl`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/[0.06]">
                    <SolarIcon name="check-circle-linear" className="h-6 w-6 text-primary" />
                </span>
                <h2 tabIndex={-1} className="mt-4 mb-2 text-2xl font-bold font-sourceSans dark:text-white outline-hidden">{t.pretTitre}</h2>
                {/* LE CHIFFRE QUI COMPTE, TOUT DE SUITE. C'est le moment où la
                    valeur doit sauter aux yeux : ce qu'il reste chaque mois,
                    avec sa source, avant le nombre d'outils remplis. */}
                {r.revenu ? (
                    <div className="mt-4">
                        <div className="text-4xl font-bold font-sourceSans tabular-nums dark:text-white">{euros(r.revenu.disponibleMensuel, lang)}</div>
                        <div className="mt-1 text-sm text-gray-600 dark:text-lightBlue">{t.pretPourVous}</div>
                        <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-white/[0.06] px-3 py-1 text-xs font-semibold text-gray-700 dark:text-lightBlue">
                            <SolarIcon name="document-text-linear" className="h-3.5 w-3.5" />
                            {t.pretSource}
                        </span>
                    </div>
                ) : (
                    <p className="mb-0 text-base text-gray-600 dark:text-lightBlue">
                        {remplis.length === 0 ? t.pretAucun : remplir(remplis.length === 1 ? t.pretTexteUn : t.pretTexte, { n: remplis.length })}
                    </p>
                )}
                {/* CE QUE NOUS AVONS DÉJÀ REPÉRÉ. C'est le moment où la valeur
                    doit sauter aux yeux : les deux premiers points de la
                    situation, avant même de l'ouvrir. */}
                {alertes.length > 0 && (
                    <div className="mt-5">
                        <p className="mb-2 text-sm font-semibold dark:text-white">{t.pretRepere}</p>
                        <div className="grid gap-2 sm:grid-cols-2">
                            {alertes.slice(0, 2).map((a) => {
                                const ton = TONS[a.gravite] || TONS.info;
                                return (
                                    <div key={a.cle} className={`rounded-xl border p-3 ${ton.cadre}`}>
                                        <div className={`${KICKER} ${ton.kicker}`}>{kickerAlerte(a, r, t, lang)}</div>
                                        <div className="mt-1 text-sm font-semibold leading-snug dark:text-white">{a.contenu.titre}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {r.revenu && remplis.length > 0 && (
                    <p className="mt-4 mb-0 text-sm text-gray-600 dark:text-lightBlue">
                        {remplir(remplis.length === 1 ? t.pretOutilsUn : t.pretOutils, { n: remplis.length })}
                    </p>
                )}
                <p className="mt-4 mb-0 flex items-start gap-2 text-sm text-gray-500 dark:text-lightBlue/70">
                    <SolarIcon name="lock-keyhole-minimalistic-linear" className="mt-0.5 h-4 w-4 shrink-0" />
                    {connecte ? t.compteSynchro : t.confidentiel}
                </p>
                <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                    <button type="button" onClick={() => setMode({ vue: "situation", question: null })} className={`${BOUTON_PLEIN} bg-[#D04515] dark:bg-[#D04515] text-white dark:text-white`}>
                        {t.pretFermer}
                    </button>
                    {!compte.connecte && (
                        <button type="button" onClick={() => setFeuilleCompte(true)} className={BOUTON_VIDE}>
                            {t.compteCreer}
                        </button>
                    )}
                </div>
                <ToolsAccountSheet lang={lang} ouvert={feuilleCompte} onFermer={() => setFeuilleCompte(false)} />
            </motion.div>
        );
    }

    // ------------------------------------------------------------------
    // La situation
    // ------------------------------------------------------------------
    const d = r.dossier;
    const enMicro = d.statut === "micro" || d.statut === "projet" || d.statut === null;
    const ouvrir = (question) => {
        setAvant({
            dossier: profil,
            disponible: r.revenu?.disponibleMensuel ?? null,
            alertes: alertes.map((a) => ({ cle: a.cle, titre: a.contenu.titre })),
        });
        setMode({ vue: "une", question });
    };
    const manques = r.manque.filter((m) => QUESTION_DU_MANQUE[m]);
    const montrerManques = manques.length > 0 && !plusTard && !plusTardActif();
    // Les outils les plus utiles à cette situation d'abord, les autres ensuite.
    const ordre = outilsDuDossier(profil);
    const rang = (cle) => (ordre.indexOf(cle) + 1 || 99);
    const nomIdentite = [identite.prenoms, identite.nom].filter(Boolean).join(" ");
    // L'entreprise choisie dans l'annuaire, si elle dit le même statut.
    const fiche = d.statut !== "projet" ? entrepriseGardee : null;

    return (
        <div className="space-y-10">
            <h2 tabIndex={-1} className="sr-only">{t.syntheseTitre}</h2>

            {/* LA FICHE DU DOSSIER, EN TÊTE. Qui l'on est (l'entreprise, ses
                réponses en étiquettes qu'on corrige d'un clic), puis le chiffre
                qui compte en grand, et le plafond ou la TVA à côté. Tout le
                reste de la page en découle. */}
            <section className="overflow-hidden rounded-2xl border border-gray-300/80 dark:border-white/15 bg-white dark:bg-white/[0.05] shadow-[0_10px_30px_rgba(17,24,39,0.05)]">
                <div className="flex items-start gap-3.5 p-5 sm:p-6">
                    <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                            d.statut === "projet" ? "bg-primary/10 text-primary" : fiche ? "bg-green-600/10 text-green-700 dark:text-green-400" : "bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-lightBlue/70"
                        }`}
                    >
                        <SolarIcon name={d.statut === "projet" ? "rocket-2-linear" : fiche ? "verified-check-bold" : "case-minimalistic-linear"} className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="mb-0 truncate text-lg font-bold font-sourceSans leading-snug text-gray-900 dark:text-white">
                            {fiche ? fiche.nom : d.statut === "projet" ? t.votreProjet : t.votreActivite}
                        </p>
                        {/* LA PHRASE DEVENUE ÉTIQUETTES : chacune ouvre la question
                            qui la change. « Vos réponses », plus bas, reste pour
                            le clavier et les lecteurs d'écran. */}
                        <ul className="m-0 mt-2 flex list-none flex-wrap gap-1.5 p-0">
                            {morceauxDossier(d, t, lang).map((m) => (
                                <li key={m.question}>
                                    <button
                                        type="button"
                                        onClick={() => ouvrir(m.question)}
                                        className="inline-flex min-h-[32px] items-center rounded-full border border-gray-200 dark:border-white/15 bg-white dark:bg-white/[0.04] px-3 text-[13px] text-gray-700 dark:text-lightBlue transition-colors duration-200 hover:border-[#D04515]/50 hover:bg-[#fff8f4] hover:text-gray-900 dark:hover:bg-white/[0.08] dark:hover:text-white"
                                    >
                                        {/* Un morceau de phrase devient une étiquette : ses
                                            parenthèses tombent, sa majuscule arrive. */}
                                        {((x) => `${x.charAt(0).toUpperCase()}${x.slice(1)}`)(m.texte.replace(/^\((.*)\)$/, "$1"))}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <button type="button" onClick={() => setMode({ vue: "parcours", question: null })} className={`${PASTILLE} hidden shrink-0 sm:inline-flex`}>
                        <SolarIcon name="pen-2-linear" className="h-4 w-4 text-gray-500 dark:text-lightBlue/70" />
                        {t.modifier}
                    </button>
                </div>

                <div className="grid border-t border-gray-200/70 dark:border-white/10 sm:grid-cols-[1.35fr_1fr]">
                    <div className="p-5 sm:p-6">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.resteTitre}</div>
                        {r.revenu ? (
                            <>
                                <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-4xl sm:text-5xl font-bold tabular-nums font-sourceSans tracking-tight text-gray-900 dark:text-white">
                                        {euros(r.revenu.disponibleMensuel, lang)}
                                    </span>
                                    <span className="text-base text-gray-500 dark:text-lightBlue/70">{t.parMois}</span>
                                </div>
                                <p className="mt-2 mb-0 text-sm text-gray-600 dark:text-lightBlue">
                                    {r.revenu.source === "micro"
                                        ? remplir(r.revenu.frais > 0 ? t.resteMicroFrais : t.resteMicro, {
                                              cotisations: euros(r.revenu.cotisations + r.revenu.cfp, lang),
                                              impot: euros(r.revenu.impot, lang),
                                              frais: euros(r.revenu.frais, lang),
                                          })
                                        : r.revenu.source === "reel"
                                          ? t.resteReel
                                          : t.resteSociete}
                                </p>
                                {r.revenu.objectif !== null && (
                                    <p
                                        className={`mt-3 mb-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold ${
                                            r.revenu.ecartMensuel < -50
                                                ? "bg-amber-50 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300"
                                                : "bg-green-50 text-green-800 dark:bg-green-500/10 dark:text-green-300"
                                        }`}
                                    >
                                        <SolarIcon name={r.revenu.ecartMensuel < -50 ? "target-linear" : "check-circle-linear"} className="h-4 w-4" />
                                        {r.revenu.ecartMensuel < -50
                                            ? remplir(t.objectifManque, { objectif: euros(r.revenu.objectif, lang), manque: euros(Math.round(-r.revenu.ecartMensuel / 10) * 10, lang) })
                                            : remplir(t.objectifAtteint, { objectif: euros(r.revenu.objectif, lang) })}
                                    </p>
                                )}
                                {enMicro && r.revenu.ecartMensuel !== null && r.revenu.ecartMensuel < -50 && (
                                    <div className="mt-1">
                                        <LienOutil outils={outils} cle="combien-facturer">
                                            {t.combienFacturer}
                                        </LienOutil>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="mt-2 mb-0 text-sm text-gray-600 dark:text-lightBlue">
                                {r.manque.includes("caCetteAnnee") || r.manque.includes("activite") ? (
                                    <button type="button" onClick={() => ouvrir(r.manque.includes("activite") ? "activite" : "ca")} className={`${LIEN} text-left`}>
                                        {r.manque.includes("activite") ? t.resteManqueActivite : t.resteManque}
                                    </button>
                                ) : (
                                    t.resteCipav
                                )}
                            </p>
                        )}
                    </div>

                    <div className="border-t border-gray-200/70 dark:border-white/10 p-5 sm:border-l sm:border-t-0 sm:p-6">
                        {r.plafond ? (
                            <>
                                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.plafondTitre}</div>
                                <div className="mt-1.5 text-3xl font-bold tabular-nums font-sourceSans text-gray-900 dark:text-white">{pourcent(r.plafond.part, lang)}</div>
                                <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.06]">
                                    <motion.div
                                        className={`h-full rounded-full ${r.plafond.part >= 0.8 ? "bg-amber-500" : "bg-[#D04515]"}`}
                                        initial={reduit ? false : { width: 0 }}
                                        animate={{ width: `${Math.min(100, Math.round(r.plafond.part * 100))}%` }}
                                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                    />
                                </div>
                                <div className="mt-2 flex justify-between gap-2 text-xs text-gray-500 dark:text-lightBlue/70">
                                    <span>{remplir(t.prevus, { montant: euros(r.plafond.ca, lang) })}</span>
                                    <span>{remplir(t.plafond, { montant: euros(r.plafond.plafond, lang) })}</span>
                                </div>
                                <p className="mt-3 mb-0 text-sm text-gray-600 dark:text-lightBlue">
                                    {r.plafond.acquis ? t.plafondAcquis : r.plafond.part < 0.8 ? t.plafondLoin : t.plafondPres}
                                </p>
                            </>
                        ) : r.tva ? (
                            <>
                                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.tvaTitre}</div>
                                {/* Où en est le lecteur, et non le seuil seul. */}
                                <div className="mt-1.5 text-3xl font-bold tabular-nums font-sourceSans text-gray-900 dark:text-white">
                                    {r.tva.statut === "deja" ? t.tvaDejaTitre : pourcent(d.caCetteAnnee / r.tva.base, lang)}
                                </div>
                                <p className="mt-2 mb-1 text-sm text-gray-600 dark:text-lightBlue">
                                    {r.tva.statut === "deja"
                                        ? t.tvaDejaTexte
                                        : r.tva.statut === "franchise"
                                          ? remplir(t.tvaFranchise, { base: euros(r.tva.base, lang) })
                                          : remplir(t.tvaAuDessus, { base: euros(r.tva.base, lang) })}
                                </p>
                                <LienOutil outils={outils} cle="seuils-tva">
                                    {t.voirCalcul}
                                </LienOutil>
                            </>
                        ) : (
                            <p className="mb-0 text-sm text-gray-600 dark:text-lightBlue">{t.echeanceManque}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200/70 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.02] px-5 py-2.5 sm:px-6 text-xs text-gray-500 dark:text-lightBlue/70">
                    <span>
                        {d.majLe ? remplir(t.misAJour, { date: dateLongue(d.majLe.slice(0, 10), lang) }) : null}
                        {d.majLe ? " · " : null}
                        {t.etiquettesAide}
                    </span>
                    <button type="button" onClick={() => setMode({ vue: "parcours", question: null })} className="inline-flex min-h-[36px] items-center gap-1 font-semibold text-gray-700 dark:text-lightBlue underline underline-offset-2 sm:hidden">
                        {t.modifier}
                    </button>
                </div>
            </section>

            {/* Une région toujours présente, pour que le bilan d'une correction
                soit annoncé aux lecteurs d'écran. */}
            <div aria-live="polite" className="empty:hidden -mt-4">
                {changements && (
                    <div className="flex items-start justify-between gap-4 rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 p-4 sm:p-5">
                        <div className="min-w-0">
                            <p className="mb-2 flex items-center gap-2 font-semibold dark:text-white">
                                <SolarIcon name="refresh-linear" className="h-4 w-4 text-primary" />
                                {t.changementsTitre}
                            </p>
                            <ul className="m-0 space-y-1 pl-6 list-disc text-sm text-gray-700 dark:text-lightBlue marker:text-gray-400">
                                {changements.map((l) => (
                                    <li key={l}>{l}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            type="button"
                            onClick={() => setAvant(null)}
                            aria-label={t.fermer}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-white/70 dark:text-lightBlue/70 dark:hover:bg-white/[0.06]"
                        >
                            <SolarIcon name="close-circle-linear" className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>

            {/* LE CHIFFRE D'AFFAIRES QUI VIEILLIT. Toute la date de la TVA en
                dépend : on demande s'il tient toujours, en un clic. */}
            {r.caAncien && (
                <div className="-mt-4 flex flex-col gap-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                    <div className="flex min-w-0 items-start gap-3">
                        <SolarIcon name="history-linear" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                        <div className="min-w-0">
                            <p className="mb-0.5 font-semibold dark:text-white">{remplir(t.caAncienTitre, { date: dateLongue(r.caAncien.le, lang) })}</p>
                            <p className="mb-0 text-sm text-gray-600 dark:text-lightBlue">{remplir(t.caAncienTexte, { montant: euros(d.caCetteAnnee, lang) })}</p>
                        </div>
                    </div>
                    <div className="flex shrink-0 gap-2 pl-8 sm:pl-0">
                        <button type="button" onClick={() => enregistrer({ caLe: new Date().toISOString().slice(0, 10) })} className={PASTILLE}>
                            {t.caJuste}
                        </button>
                        <button type="button" onClick={() => ouvrir("ca")} className={`${PASTILLE} !border-[#D04515] !bg-[#D04515] !text-white hover:!bg-[#B83C12]`}>
                            {t.caCorriger}
                        </button>
                    </div>
                </div>
            )}

            {/* LES RÉPONSES QUI MANQUENT, AVEC CE QU'ELLES APPORTENT. Plutôt
                qu'un « 4 sur 6 », ce que chacune rendrait juste, et combien
                d'outils elle remplirait. */}
            {montrerManques && (
                <section className="rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex min-w-0 items-start gap-3">
                            <span className="relative flex h-11 w-11 shrink-0 items-center justify-center">
                                <svg viewBox="0 0 36 36" className="h-11 w-11 -rotate-90" aria-hidden="true">
                                    <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3.5" className="stroke-primary/15" />
                                    <circle
                                        cx="18"
                                        cy="18"
                                        r="15"
                                        fill="none"
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        className="stroke-[#D04515]"
                                        strokeDasharray={`${((6 - r.manque.length) / 6) * 94.2} 94.2`}
                                    />
                                </svg>
                                <span className="absolute text-[11px] font-bold tabular-nums text-[#B83C12] dark:text-[#FF8A5B]">{6 - r.manque.length}/6</span>
                            </span>
                            <div className="min-w-0">
                                <h2 className="mb-0.5 text-base font-bold dark:text-white">
                                    {manques.length === 1 ? t.completerTitreUn : remplir(t.completerTitre, { n: manques.length })}
                                </h2>
                                <p className="mb-0 text-sm text-gray-600 dark:text-lightBlue">{t.completerIntro}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                writeLocal(CLE_PLUS_TARD, new Date().toISOString());
                                setPlusTard(true);
                            }}
                            className="shrink-0 inline-flex min-h-[44px] items-center text-sm text-gray-500 dark:text-lightBlue/70 underline underline-offset-2"
                        >
                            {t.completerPlusTard}
                        </button>
                    </div>
                    <ul className="m-0 mt-3 list-none space-y-2 p-0">
                        {manques.map((m) => (
                            <li key={m}>
                                <button
                                    type="button"
                                    onClick={() => ouvrir(QUESTION_DU_MANQUE[m])}
                                    className="group flex w-full items-center justify-between gap-4 rounded-xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-3 text-left transition-colors duration-200 hover:border-[#D04515]/50"
                                >
                                    <span className="min-w-0 text-sm text-gray-700 dark:text-lightBlue">
                                        {t.completerGains[m]}
                                        <span className="text-gray-500 dark:text-lightBlue/70"> · {remplir(lang === "en" ? "{n} tools" : "{n} outils", { n: OUTILS_PAR_MANQUE[m] })}</span>
                                    </span>
                                    <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-[#B83C12] dark:text-[#FF8A5B]">
                                        {t.repondre}
                                        <SolarIcon name="arrow-right-linear" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* À REGARDER : une liste, pas une mosaïque. Chaque point a son
                repère de couleur (rouge qui presse, ambre qui approche, bleu
                qui informe), son titre, ce qu'il faut savoir, et l'outil qui
                le traite. */}
            <section aria-labelledby="situation-regarder">
                <h2 id="situation-regarder" className={TITRE_SECTION}>
                    <SolarIcon name="eye-linear" className="h-4 w-4" />
                    {t.aRegarder}
                    {alertes.length > 0 && (
                        <span className="rounded-full bg-gray-900 dark:bg-white px-1.5 text-[11px] font-bold tabular-nums text-white dark:text-gray-900">{alertes.length}</span>
                    )}
                </h2>
                <div className={LISTE}>
                    {alertes.length === 0 ? (
                        <p className="m-0 flex items-center gap-2.5 px-5 py-4 text-sm text-gray-600 dark:text-lightBlue">
                            <SolarIcon name="check-circle-linear" className="h-5 w-5 text-green-600 dark:text-green-400" />
                            {t.rienARegarder}
                        </p>
                    ) : (
                        <ul className="m-0 list-none divide-y divide-gray-200/60 dark:divide-white/[0.07] p-0">
                            {alertes.map((a) => {
                                const ton = TONS[a.gravite] || TONS.info;
                                return (
                                    <li key={a.cle} className="flex gap-3.5 px-4 py-4 sm:px-5">
                                        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${ton.point}`} aria-hidden="true" />
                                        <div className="min-w-0 flex-1">
                                            <div className={`text-xs font-bold uppercase tracking-wider ${ton.kicker}`}>{kickerAlerte(a, r, t, lang)}</div>
                                            <h3 className="mt-1 mb-1 text-base font-bold font-sourceSans leading-snug text-gray-900 dark:text-white">{a.contenu.titre}</h3>
                                            <p className="mb-1 text-sm leading-6 text-gray-600 dark:text-lightBlue">{a.contenu.texte}</p>
                                            <div className="flex flex-wrap items-center gap-x-5">
                                                <LienOutil outils={outils} cle={a.contenu.outil || a.outil}>
                                                    {a.contenu.lien === t.voirCalcul && outils[a.contenu.outil || a.outil]
                                                        ? remplir(t.voirCalculDans, { outil: outils[a.contenu.outil || a.outil].nom })
                                                        : a.contenu.lien}
                                                </LienOutil>
                                                {/* LA TVA ANNONCÉE À QUI LA FACTURE DÉJÀ : une réponse,
                                                    gardée dans le dossier, et l'annonce disparaît. */}
                                                {a.cle === "tva" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => enregistrer({ tvaFacturee: true })}
                                                        className="inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-gray-500 dark:text-lightBlue/70 hover:text-gray-900 dark:hover:text-white"
                                                    >
                                                        <SolarIcon name="bill-check-linear" className="h-4 w-4" />
                                                        {t.tvaDejaBouton}
                                                    </button>
                                                )}
                                                {a.fait && (
                                                    <button
                                                        type="button"
                                                        onClick={() => marquerFait(a.fait, a.contenu.titre)}
                                                        className="inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-gray-500 dark:text-lightBlue/70 hover:text-gray-900 dark:hover:text-white"
                                                    >
                                                        <SolarIcon name="check-circle-linear" className="h-4 w-4" />
                                                        {t.cestFait}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {/* EN ORDRE : ce que le lecteur a déclaré fait, en pied de
                        liste, qu'il peut remettre dans ses points à faire. */}
                    {r.enOrdre.length > 0 && (
                        <details className="group border-t border-gray-200/60 dark:border-white/[0.07] px-5">
                            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-4 py-2 text-sm font-semibold text-gray-700 dark:text-lightBlue">
                                <span className="flex items-center gap-2">
                                    <SolarIcon name="check-circle-linear" className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    {remplir(t.enOrdreTitre, { n: r.enOrdre.length })}
                                </span>
                                <SolarIcon name="alt-arrow-down-linear" className="h-4 w-4 transition-transform duration-200 group-open:rotate-180" />
                            </summary>
                            <ul className="m-0 list-none space-y-1 p-0 pb-3">
                                {r.enOrdre.map((e) => (
                                    <li key={e.fait} className="flex items-center justify-between gap-4 text-sm">
                                        <span className="min-w-0 text-gray-600 dark:text-lightBlue">
                                            {libelleEcheance(e.cle, e.famille, lang)}
                                            <span className="text-gray-500 dark:text-lightBlue/70"> · {remplir(t.enOrdreLe, { date: dateLongue(e.le, lang) })}</span>
                                        </span>
                                        <button type="button" onClick={() => remettre(e.fait)} className="shrink-0 inline-flex min-h-[44px] items-center text-xs underline underline-offset-2 text-gray-500 dark:text-lightBlue/70">
                                            {t.remettre}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            </section>

            <Agenda r={r} t={t} lang={lang} outils={outils} compte={compte} confirmer={setConfirmation} marquerFait={marquerFait} />

            {/* LE VRAI SOLDE, puis LA CARTE À PARTAGER, qui en reprend la part. */}
            <ToolsVraiSolde lang={lang} dossier={profil} aujourdhui={aujourdhui} onPart={setPartVraie} />
            <ToolsShareCard lang={lang} r={r} part={partVraie} />

            {r.statutConseil && r.statutConseil.type === "garder" && (
                <section className="flex items-start gap-3.5 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-5 sm:p-6">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-lightBlue/70">
                        <SolarIcon name="scale-linear" className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70">{t.statutTitre}</div>
                        <h2 className="mt-1 mb-1.5 text-base sm:text-lg font-bold font-sourceSans dark:text-white">
                            {remplir(t.statutGarderTitre, { actuel: t.regimesArticle[r.statutConseil.actuel] })}
                        </h2>
                        <p className="mb-1 text-sm text-gray-600 dark:text-lightBlue">
                            {remplir(enMicro || d.statut === "reel" ? t.statutGarderTexte : t.statutGarderSociete, {
                                frais: euros((d.fraisMensuels || 0) * 12, lang),
                                netActuel: euros(r.statutConseil.nets[r.statutConseil.actuel], lang),
                                actuelCourt: t.regimes[r.statutConseil.actuel],
                                netSecond: euros(r.statutConseil.nets[r.statutConseil.second], lang),
                                second: t.regimes[r.statutConseil.second],
                            })}
                        </p>
                        <LienOutil outils={outils} cle={enMicro || d.statut === "reel" ? "micro-reel-ou-societe" : "eurl-ou-sasu"}>
                            {t.ouvrirComparatif}
                        </LienOutil>
                    </div>
                </section>
            )}

            {/* LES OUTILS REMPLIS : les mêmes lignes que la liste de /outils,
                les plus utiles à cette situation d'abord. */}
            {remplis.length > 0 && (
                <section aria-labelledby="situation-outils">
                    <h2 id="situation-outils" className={TITRE_SECTION}>
                        <SolarIcon name="widget-linear" className="h-4 w-4" />
                        {t.outilsTitre}
                    </h2>
                    <ul className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2">
                        {[...remplis]
                            .sort((a, b) => rang(a.cle) - rang(b.cle))
                            .slice(0, 10)
                            .map((o) => (
                                <li key={o.cle}>
                                    <Link
                                        href={{ pathname: "/tools/[slug]", params: { slug: o.slug } }}
                                        className="group flex min-h-[52px] items-center gap-3 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] px-3.5 py-2.5 transition-colors duration-200 hover:border-[#D04515]/40 hover:bg-white dark:hover:bg-white/[0.05]"
                                    >
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/[0.07] dark:bg-primary/[0.12]">
                                            <SolarIcon name={o.icone || "calculator-minimalistic-linear"} className="h-4 w-4 text-primary" />
                                        </span>
                                        <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900 dark:text-white group-hover:text-[#B83C12] dark:group-hover:text-[#FF8A5B]">{o.nom}</span>
                                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 dark:bg-green-500/10 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:text-green-400">
                                            <SolarIcon name="check-read-linear" className="h-3 w-3" />
                                            {t.rempli}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                    </ul>
                </section>
            )}

            {/* VOTRE DOSSIER : les réponses repliées, l'identité des
                formulaires, et le compte (ou, connecté, les alertes). */}
            <section aria-labelledby="situation-dossier" className="space-y-3">
                <h2 id="situation-dossier" className={TITRE_SECTION}>
                    <SolarIcon name="folder-with-files-linear" className="h-4 w-4" />
                    {t.reponsesTitre}
                </h2>
                <details className={`group ${LISTE}`}>
                    <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-4 px-5">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{remplir(t.reponsesCompte, { n: 6 - r.manque.length })}</span>
                        <SolarIcon name="alt-arrow-down-linear" className="h-4 w-4 text-gray-400 transition-transform duration-200 group-open:rotate-180" />
                    </summary>
                    <dl className="m-0 divide-y divide-gray-200/60 dark:divide-white/[0.07] border-t border-gray-200/60 dark:border-white/[0.07] px-5">
                        {["statut", "activite", "debut", "ca", "foyer", "objectif"].map((q) => {
                            const valeur = valeurQuestion(q, d, t, lang);
                            return (
                                <div key={q} className="flex items-center justify-between gap-4 py-2">
                                    <div className="min-w-0">
                                        <dt className="text-xs text-gray-500 dark:text-lightBlue/70">{t.questions[q]}</dt>
                                        <dd className={`m-0 text-sm ${valeur ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-lightBlue/70"}`}>{valeur || t.nonRenseigne}</dd>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => ouvrir(q)}
                                        className="shrink-0 inline-flex min-h-[44px] items-center text-sm font-semibold text-gray-900 dark:text-white underline underline-offset-2 decoration-gray-300 dark:decoration-white/30 hover:decoration-primary"
                                    >
                                        {valeur ? t.modifierCourt : t.repondre}
                                        {/* Six boutons « Modifier » : chacun dit ce qu'il modifie. */}
                                        <span className="sr-only"> : {t.questions[q]}</span>
                                    </button>
                                </div>
                            );
                        })}
                        {/* LA TVA, UNE RÉPONSE DE PLUS : oui ou non d'un geste. */}
                        {d.statut !== "projet" && (
                            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2.5">
                                <div className="min-w-0">
                                    <dt className="text-xs text-gray-500 dark:text-lightBlue/70">{t.tvaQuestion}</dt>
                                    <dd className={`m-0 text-sm ${d.tvaFacturee === null ? "text-gray-500 dark:text-lightBlue/70" : "text-gray-900 dark:text-white"}`}>
                                        {d.tvaFacturee === true ? t.tvaOui : d.tvaFacturee === false ? t.tvaNon : t.nonRenseigne}
                                    </dd>
                                </div>
                                <div role="group" aria-label={t.tvaQuestion} className="flex shrink-0 gap-1.5">
                                    {[
                                        [true, t.tvaOui],
                                        [false, t.tvaNon],
                                    ].map(([valeur, libelle]) => (
                                        <button
                                            key={String(valeur)}
                                            type="button"
                                            aria-pressed={d.tvaFacturee === valeur}
                                            onClick={() => enregistrer({ tvaFacturee: valeur })}
                                            className={`inline-flex min-h-[36px] items-center rounded-full border px-3 text-[13px] font-semibold transition-colors duration-200 ${
                                                d.tvaFacturee === valeur
                                                    ? "border-[#D04515] bg-[#fff4ee] text-[#B83C12] dark:bg-[#D04515]/15 dark:text-[#FF8A5B]"
                                                    : "border-gray-300 bg-white text-gray-700 hover:border-[#D04515] dark:border-white/20 dark:bg-transparent dark:text-lightBlue"
                                            }`}
                                        >
                                            {libelle}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </dl>
                </details>

                {/* DEUX CARTES DE MÊME POIDS : ce qui reste sur l'appareil
                    (l'identité, montrée par ce qu'elle contient, en coches) et
                    ce qu'apporte un compte (trois bénéfices, pas un pavé).
                    L'action de chacune au même endroit, en bas. */}
                <div className="grid gap-3 md:grid-cols-2">
                    <div className="flex flex-col rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-lightBlue/70">
                                <SolarIcon name="user-id-linear" className="h-5 w-5" />
                            </span>
                            <h3 className="mb-0 text-base font-bold font-sourceSans leading-snug dark:text-white">{t.identiteTitre}</h3>
                        </div>
                        {identiteVide(identite) ? (
                            <p className="mt-3 mb-0 text-sm text-gray-600 dark:text-lightBlue">{t.identiteVide}</p>
                        ) : (
                            <>
                                {nomIdentite && <p className="mt-3 mb-0 font-semibold text-gray-900 dark:text-white">{nomIdentite}</p>}
                                <ul className="m-0 mt-2.5 flex list-none flex-wrap gap-1.5 p-0">
                                    {[
                                        ["nom", identite.prenoms || identite.nom],
                                        ["adresse", identite.voie || identite.commune],
                                        ["naissance", identite.dateNaissance],
                                        ["siret", identite.siret],
                                        ["contact", identite.email || identite.telephone],
                                    ].map(([cle, rempli]) => (
                                        <li
                                            key={cle}
                                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                                                rempli ? "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400" : "bg-gray-100 text-gray-400 dark:bg-white/[0.05] dark:text-lightBlue/50"
                                            }`}
                                        >
                                            <SolarIcon name={rempli ? "check-circle-linear" : "minus-circle-linear"} className="h-3.5 w-3.5" />
                                            {t.identiteChamps[cle]}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2.5 mb-0 text-[13px] text-gray-500 dark:text-lightBlue/70">{t.identiteGardee}</p>
                            </>
                        )}
                        <div className="mt-auto pt-4">
                            <button type="button" onClick={() => setFeuille(true)} className={PASTILLE}>
                                <SolarIcon name={identiteVide(identite) ? "add-circle-linear" : "pen-2-linear"} className="h-4 w-4 text-gray-500 dark:text-lightBlue/70" />
                                {identiteVide(identite) ? t.identiteEnregistrer : t.identiteModifier}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col rounded-2xl border border-primary/25 bg-[#fff8f4] dark:bg-[#D04515]/10 p-5">
                        {compte.connecte ? (
                            <>
                                <AlerteRegles t={t} lang={lang} dossier={profil} />
                                <p className="mt-3 mb-0 text-sm text-gray-500 dark:text-lightBlue/70">{t.compteSynchro}</p>
                            </>
                        ) : (
                            !compte.chargement && (
                                <>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                            <SolarIcon name="cloud-check-linear" className="h-5 w-5" />
                                        </span>
                                        <h3 className="mb-0 text-base font-bold font-sourceSans leading-snug dark:text-white">{t.compteTitre}</h3>
                                    </div>
                                    <ul className="m-0 mt-3 list-none space-y-1.5 p-0">
                                        {t.compteAvantages.map((a) => (
                                            <li key={a} className="flex items-center gap-2 text-sm text-gray-700 dark:text-lightBlue">
                                                <SolarIcon name="check-circle-linear" className="h-4 w-4 shrink-0 text-primary" />
                                                {a}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setFeuilleCompte(true)}
                                            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-[#D04515] px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#B83C12]"
                                        >
                                            <SolarIcon name="letter-linear" className="h-4 w-4" />
                                            {t.compteCreer}
                                        </button>
                                        <span className="text-[13px] text-gray-500 dark:text-lightBlue/70">{t.compteSansMotDePasse}</span>
                                    </div>
                                    <ToolsAccountSheet lang={lang} ouvert={feuilleCompte} onFermer={() => setFeuilleCompte(false)} />
                                </>
                            )
                        )}
                    </div>
                </div>
            </section>

            <div className="flex flex-col gap-2 border-t border-gray-200/70 dark:border-white/10 pt-4 text-xs text-gray-500 dark:text-lightBlue/70 sm:flex-row sm:items-center sm:justify-between">
                <span>{t.pied}</span>
                <button
                    type="button"
                    onClick={() =>
                        setConfirmation({
                            titre: t.effacerTitre,
                            texte: t.effacerConfirmer,
                            oui: t.effacerOui,
                            danger: true,
                            onOui: () => {
                                effacer();
                                setMode({ vue: null, question: null });
                            },
                        })
                    }
                    className="shrink-0 inline-flex min-h-[44px] items-center underline underline-offset-2 hover:text-red-600 dark:hover:text-red-400"
                >
                    {t.effacer}
                </button>
            </div>

            <ToolsIdentitySheet lang={lang} ouvert={feuille} onFermer={() => setFeuille(false)} />
            <Confirmer demande={confirmation} onFermer={() => setConfirmation(null)} t={t} />
        </div>
    );
    }
}
